import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/** The parts of a mouse event that decide where a click opens. */
export interface ClickModifiers {
    button?: number;
    metaKey?: boolean;
    ctrlKey?: boolean;
    shiftKey?: boolean;
    preventDefault?: () => void;
}

/** Cmd-click (Mac), Ctrl-click, Shift-click or a middle click: the user wants a new tab. */
export const isNewTabClick = (event: ClickModifiers): boolean =>
    event.button === 1 || Boolean(event.metaKey || event.ctrlKey || event.shiftKey);

/**
 * Opens an app path from a click on something that cannot be a real link
 * (a table row, a map or drawing pin, a card with buttons inside).
 * Prefer `<Link>` (or react-bootstrap `as={Link}`) wherever the element can be one:
 * only a real link gets right-click "Öppna i ny flik" and the URL on hover.
 *
 * Wire it to both `onClick` and `onAuxClick` so a middle click works too:
 *   onClick={e => openPath(path, e)} onAuxClick={e => openPath(path, e)}
 * Without an event (keyboard, programmatic) it navigates in the same tab.
 */
export function useOpenPath(): (path: string, event?: ClickModifiers) => void {
    const navigate = useNavigate();
    return useCallback((path: string, event?: ClickModifiers) => {
        // auxclick also fires for the right button; that one opens the context menu
        if (event?.button !== undefined && event.button > 1) return;
        if (event && isNewTabClick(event)) {
            event.preventDefault?.();
            window.open(path, '_blank', 'noopener');
            return;
        }
        navigate(path);
    }, [navigate]);
}
